import {
  Box, Card, CardContent, Chip, LinearProgress, Stack, Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import LayersIcon from '@mui/icons-material/Layers';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { regOps } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type { MedicalJurisdiction } from '@/types/medical';

type ReleaseStatus = 'draft' | 'tested' | 'approved' | 'active' | 'retired' | 'rolled_back';

type Release = {
  id: number;
  jurisdiction: number;
  version: string;
  status: ReleaseStatus;
  rules: number[];
  sources: number[];
  coverage_score: string | null;
  test_pass_rate: string | null;
  approved_by: number | null;
  approved_at: string | null;
  activated_at: string | null;
  retired_at: string | null;
  changelog: string;
  created_at: string;
  updated_at: string;
};

const STATUS_META: Record<ReleaseStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
  draft:       { label: 'draft',       color: 'default' },
  tested:      { label: 'tested',      color: 'primary' },
  approved:    { label: 'approved',    color: 'primary' },
  active:      { label: 'ACTIVE',      color: 'success' },
  retired:     { label: 'retired',     color: 'default' },
  rolled_back: { label: 'rolled back', color: 'error' },
};

export function RegOpsReleasesPage() {
  const [params] = useSearchParams();
  const filterJurisdictionId = params.get('jurisdiction');

  const releasesQ = useApiQuery({
    queryKey: ['regops', 'releases'],
    queryFn:  regOps.releases.list as () => Promise<Release[]>,
  });
  const jurisdictionsQ = useApiQuery({
    queryKey: ['regops', 'jurisdictions'],
    queryFn:  regOps.jurisdictions.list as () => Promise<MedicalJurisdiction[]>,
  });

  const filtered = useMemo(() => {
    const all = releasesQ.data ?? [];
    if (!filterJurisdictionId) return all;
    return all.filter((r) => r.jurisdiction === parseInt(filterJurisdictionId, 10));
  }, [releasesQ.data, filterJurisdictionId]);

  const jurisdictionMap = useMemo(() => {
    const m = new Map<number, MedicalJurisdiction>();
    (jurisdictionsQ.data ?? []).forEach((j) => m.set(j.id, j));
    return m;
  }, [jurisdictionsQ.data]);

  return (
    <>
      <PageHeader
        title="Rule set releases"
        subtitle="Versioned releases of rules + sources, with test pass rates and approval flow."
      />

      <DataState query={releasesQ} empty={{ title: 'No releases yet' }}>
        {() => filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No releases for this filter. Releases are created from a draft set of rules.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filtered
              .slice()
              .sort((a, b) => b.id - a.id)
              .map((r) => (
                <Row key={r.id} release={r}
                     jurisdictionCode={jurisdictionMap.get(r.jurisdiction)?.code ?? '?'} />
              ))}
          </Stack>
        )}
      </DataState>
    </>
  );
}

function Row({
  release, jurisdictionCode,
}: { release: Release; jurisdictionCode: string }) {
  const meta = STATUS_META[release.status] ?? STATUS_META.draft;
  const passRate = release.test_pass_rate ? parseFloat(release.test_pass_rate) : null;

  return (
    <Card
      component={RouterLink}
      to={`/regulation-ops/releases/${release.id}`}
      sx={{ textDecoration: 'none', color: 'inherit',
            '&:hover': { borderColor: 'primary.main' },
            borderColor: release.status === 'active' ? 'success.main' : 'divider' }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <LayersIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} className="tabular">
                v{release.version}
              </Typography>
              <Chip size="small" label={jurisdictionCode}
                    variant="outlined" className="tabular" />
              <Chip size="small" label={meta.label} color={meta.color}
                    variant={release.status === 'active' ? 'filled' : 'outlined'} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {release.rules?.length ?? 0} rule(s) ·{' '}
              <span className="tabular">created {formatDateTime(release.created_at)}</span>
              {release.activated_at &&
                <> · activated <span className="tabular">{formatDateTime(release.activated_at)}</span></>}
            </Typography>
            {passRate != null && (
              <Box sx={{ mt: 1, maxWidth: 280 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">Tests</Typography>
                  <Typography variant="caption" className="tabular" fontWeight={600}>
                    {Math.round(passRate * 100)}%
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={passRate * 100}
                                sx={{ height: 4, borderRadius: 2,
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: passRate >= 1 ? 'success.main'
                                               : passRate >= 0.8 ? 'warning.main'
                                               : 'error.main',
                                      } }} />
              </Box>
            )}
          </Box>
          <ChevronRightIcon color="action" />
        </Stack>
      </CardContent>
    </Card>
  );
}
